import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { productCategoryDto } from 'src/app/model/category/productCategoryDto';
import { CommonFeatureInterface } from 'src/app/model/commonFeature/common-feature-interface';
import { LinkingMethod } from 'src/app/model/enums/linkingMethod';
import { CategoryService } from 'src/app/services/dataServices/product-service/categoryService';

@Component({
    selector: 'app-common-feature-item-edit-form',
    templateUrl: './common-feature-item-edit-form.component.html',
    styleUrls: ['./common-feature-item-edit-form.component.css'],
    standalone: false
})
export class CommonFeatureItemEditFormComponent implements OnInit {
    @Input()
    public commonFeature: CommonFeatureInterface;

    @Output() close = new EventEmitter<'ABORT' | 'SAVE' | 'DELETE'>();

    readonly linkingMethods = [
        LinkingMethod.AND,
        LinkingMethod.OR,
        LinkingMethod.XOR
    ];
    form: UntypedFormGroup;

    get entries(): UntypedFormArray {
        return this.form.get('entries') as UntypedFormArray;
    }
    public categories: productCategoryDto[] = [];
    private initialState;
    
    constructor(private categoryService: CategoryService) {       
    }

    ngOnInit() {
        this.categories = this.categoryService.categories$.getValue();
        const selectedCategory = this.commonFeature.categories?.[0];
        const defaultCategory = this.categories.find(c => c.name === selectedCategory)?.name || '';
        this.form = new UntypedFormGroup({
            commonFeatureName: new UntypedFormControl(
                this.commonFeature.name,
                Validators.required
            ),
            filterOption: new UntypedFormControl(this.commonFeature.filterOption),
            showDetailPage: new UntypedFormControl(this.commonFeature.showDetailPage),
            linkingMethod: new UntypedFormControl(
                this.commonFeature.linkingMethod,
                Validators.required
            ),
            entries: new UntypedFormArray(
                this.commonFeature.featureEntries
                    .sort((a, b) =>
                        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
                    )
                    .map(
                        (entry) =>
                            new UntypedFormGroup({
                                id: new UntypedFormControl(entry.id),
                                name: new UntypedFormControl(
                                    entry.name,
                                    Validators.required
                                )
                            })
                    ),
                (formArray: UntypedFormArray) =>
                    formArray.controls.length > 0 ? null : { required: true }
            ),
            appKategorie: new UntypedFormArray(
                this.categories.map(cat =>
                    new UntypedFormControl(
                        this.commonFeature.categories?.includes(cat.name)
                    )
                )
            ),
        });

        if (this.entries.length == 0) {
            this.addFeatureEntry();
        }
        this.initialState = this.form.value;
    }

    getLocalizedLinkingMethodButtonText(linkingMethod: LinkingMethod): string {
        switch (linkingMethod) {
            case LinkingMethod.AND:
                return 'UND';
            case LinkingMethod.OR:
                return 'ODER';
            case LinkingMethod.XOR:
                return 'EXKLUSIV-ODER';
        }
    }

    addFeatureEntry(): void {
        this.entries.push(
            new UntypedFormGroup({
                id: new UntypedFormControl(null),
                name: new UntypedFormControl('', Validators.required)
            })
        );
    }

    removeFeatureEntry(index: number): void {
        this.entries.removeAt(index);
    }

    isFeatureNameInvalid(): boolean {
        return this.form.get('commonFeatureName').invalid;
    }

    areEntriesInvalid(): boolean {
        return this.entries.invalid && this.entries.length === 0;
    }

    isEntryInvalid(index: number): boolean {
        return this.entries.at(index).invalid;
    }

    save(): void {
        this.commonFeature.name = this.form.get('commonFeatureName').value;
        this.commonFeature.filterOption = this.form.get('filterOption').value;
        const selectedCategoryControls = this.form.get('appKategorie') as UntypedFormArray;
        this.commonFeature.categories = this.categories
            .filter((_, index) => selectedCategoryControls.at(index).value)
            .map(cat => cat.name);
        this.commonFeature.showDetailPage =
            this.form.get('showDetailPage').value;
        this.commonFeature.linkingMethod = this.form.get('linkingMethod').value;
        const ids = [];
        for (const entry of this.entries.controls) {
            const id = entry.get('id').value;
            ids.push(id);
            const name = entry.get('name').value;
            if (id != null) {
                const featureEntry = this.commonFeature.featureEntries.find(
                    (featureEntry) => featureEntry.id == id
                );
                featureEntry.name = name;
            } else {
                this.commonFeature.featureEntries.push({
                    id: null,
                    name: name,
                    commonFeatureDtoId: this.commonFeature.id
                });
            }
        }
        this.commonFeature.featureEntries =
            this.commonFeature.featureEntries.filter((featureEntry) =>
                ids.includes(featureEntry.id)
            );
        this.close.emit('SAVE');
    }

    cancel(): void {
        this.form.reset(this.initialState);
        this.close.emit('ABORT');
    }

    delete(): void {
        this.close.emit('DELETE');
    }
}
